import { useCallback, useState } from 'react';
import {
  applyTypeDefaults,
  blankSchemaDraft,
  newFieldDraft,
  newSectionDraft,
  type FieldDraft,
  type SchemaDraft,
  type SectionDraft,
  slugLabel,
} from './schema-draft';
import { attachToSupportsSearchable } from '@/models/form-schema';
import type { FormFieldOptionData, FormFieldShowIfData, FormFieldType } from '@/services/api-types';

/** Reorder utility — moves index `from` to `to` (clamps); returns a new array. */
function move<T>(arr: T[], from: number, to: number): T[] {
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  const target = Math.max(0, Math.min(to, next.length));
  next.splice(target, 0, item);
  return next;
}

export interface UseSchemaDraftResult {
  draft: SchemaDraft;
  setDraft: (next: SchemaDraft) => void;
  reset: () => void;

  // Schema-level
  setName: (name: string) => void;
  setAttachTo: (attachTo: string) => void;

  // Sections
  addSection: () => string;
  removeSection: (uiId: string) => void;
  moveSection: (uiId: string, delta: 1 | -1) => void;
  updateSection: (uiId: string, patch: Partial<Omit<SectionDraft, 'uiId' | 'fields'>>) => void;

  // Fields (within a section)
  addField: (sectionUiId: string, type?: FormFieldType) => void;
  removeField: (sectionUiId: string, fieldUiId: string) => void;
  moveField: (sectionUiId: string, fieldUiId: string, delta: 1 | -1) => void;
  updateField: (sectionUiId: string, fieldUiId: string, patch: Partial<FieldDraft>) => void;
  changeFieldType: (sectionUiId: string, fieldUiId: string, type: FormFieldType) => void;

  // Field-options (radio/select/checkboxGroup)
  addOption: (sectionUiId: string, fieldUiId: string) => void;
  removeOption: (sectionUiId: string, fieldUiId: string, idx: number) => void;
  moveOption: (sectionUiId: string, fieldUiId: string, idx: number, delta: 1 | -1) => void;
  updateOption: (sectionUiId: string, fieldUiId: string, idx: number, patch: Partial<FormFieldOptionData>) => void;

  // ShowIf
  setShowIf: (sectionUiId: string, fieldUiId: string, showIf: FormFieldShowIfData | undefined) => void;

  // Formula
  setFormula: (sectionUiId: string, fieldUiId: string, formula: string) => void;

  // Table columns — targets a TABLE field by (sectionUiId, fieldUiId).
  addColumn: (sectionUiId: string, fieldUiId: string) => void;
  removeColumn: (sectionUiId: string, fieldUiId: string, columnUiId: string) => void;
  moveColumn: (sectionUiId: string, fieldUiId: string, columnUiId: string, delta: 1 | -1) => void;
  updateColumn: (sectionUiId: string, fieldUiId: string, columnUiId: string, patch: Partial<FieldDraft>) => void;
  changeColumnType: (sectionUiId: string, fieldUiId: string, columnUiId: string, type: FormFieldType) => void;
}

export function useSchemaDraft(initial: SchemaDraft = blankSchemaDraft()): UseSchemaDraftResult {
  const [draft, setDraft] = useState<SchemaDraft>(initial);

  const reset = useCallback(() => setDraft(blankSchemaDraft()), []);

  // ── Schema-level ───────────────────────────────────────────────
  const setName = useCallback((name: string) => {
    setDraft((d) => ({ ...d, name }));
  }, []);
  const setAttachTo = useCallback((attachTo: string) => {
    setDraft((d) => {
      // Case report forms are not part of Records Search, and the backend
      // rejects a schema that marks any field searchable. Drop the flag here
      // so switching host type can't produce an unexplained save failure.
      if (attachToSupportsSearchable(attachTo)) return { ...d, attachTo };
      return {
        ...d,
        attachTo,
        sections: d.sections.map((s) => ({
          ...s,
          fields: s.fields.map((f) => (f.searchable ? { ...f, searchable: undefined } : f)),
        })),
      };
    });
  }, []);

  // ── Sections ───────────────────────────────────────────────────
  const addSection = useCallback(() => {
    const next = newSectionDraft();
    setDraft((d) => {
      // Seed a default title (and the auto-slugged id) so the section is valid on save
      // without the admin having to type anything. Index-based so multiple new sections
      // don't collide on the auto-generated id. The admin can rename in the inspector;
      // updateSection's auto-slug keeps id in sync as long as idLocked stays false.
      const ordinal = d.sections.length + 1;
      next.title = `New section ${ordinal}`;
      next.id = slugLabel(next.title);
      return { ...d, sections: [...d.sections, next] };
    });
    return next.uiId;
  }, []);
  const removeSection = useCallback((uiId: string) => {
    setDraft((d) => ({ ...d, sections: d.sections.filter((s) => s.uiId !== uiId) }));
  }, []);
  const moveSection = useCallback((uiId: string, delta: 1 | -1) => {
    setDraft((d) => {
      const idx = d.sections.findIndex((s) => s.uiId === uiId);
      if (idx < 0) return d;
      return { ...d, sections: move(d.sections, idx, idx + delta) };
    });
  }, []);
  const updateSection = useCallback(
    (uiId: string, patch: Partial<Omit<SectionDraft, 'uiId' | 'fields'>>) => {
      setDraft((d) => ({
        ...d,
        sections: d.sections.map((s) => {
          if (s.uiId !== uiId) return s;
          let merged: SectionDraft = { ...s, ...patch };
          // Auto-slug: when title changes and id isn't locked, derive id from title.
          if (Object.prototype.hasOwnProperty.call(patch, 'title') && !merged.idLocked) {
            merged.id = slugLabel(merged.title);
          }
          return merged;
        }),
      }));
    },
    [],
  );

  // ── Fields ─────────────────────────────────────────────────────
  const updateSectionFields = useCallback(
    (sectionUiId: string, mutate: (fields: FieldDraft[]) => FieldDraft[]) => {
      setDraft((d) => ({
        ...d,
        sections: d.sections.map((s) => (s.uiId === sectionUiId ? { ...s, fields: mutate(s.fields) } : s)),
      }));
    },
    [],
  );

  const addField = useCallback((sectionUiId: string, type: FormFieldType = 'text') => {
    updateSectionFields(sectionUiId, (fields) => [...fields, newFieldDraft(type)]);
  }, [updateSectionFields]);

  const removeField = useCallback((sectionUiId: string, fieldUiId: string) => {
    updateSectionFields(sectionUiId, (fields) => fields.filter((f) => f.uiId !== fieldUiId));
  }, [updateSectionFields]);

  const moveField = useCallback((sectionUiId: string, fieldUiId: string, delta: 1 | -1) => {
    updateSectionFields(sectionUiId, (fields) => {
      const idx = fields.findIndex((f) => f.uiId === fieldUiId);
      if (idx < 0) return fields;
      return move(fields, idx, idx + delta);
    });
  }, [updateSectionFields]);

  const updateField = useCallback(
    (sectionUiId: string, fieldUiId: string, patch: Partial<FieldDraft>) => {
      updateSectionFields(sectionUiId, (fields) =>
        fields.map((f) => {
          if (f.uiId !== fieldUiId) return f;
          let merged: FieldDraft = { ...f, ...patch };
          // Auto-slug: when label changes and key isn't locked, derive key from label.
          if (Object.prototype.hasOwnProperty.call(patch, 'label') && !merged.keyLocked) {
            merged.key = slugLabel(merged.label);
          }
          return merged;
        }),
      );
    },
    [updateSectionFields],
  );

  const changeFieldType = useCallback(
    (sectionUiId: string, fieldUiId: string, type: FormFieldType) => {
      updateSectionFields(sectionUiId, (fields) =>
        fields.map((f) => (f.uiId === fieldUiId ? applyTypeDefaults({ ...f, type }) : f)),
      );
    },
    [updateSectionFields],
  );

  // ── Field-options (radio/select/checkboxGroup) ─────────────────
  const updateFieldOptions = useCallback(
    (sectionUiId: string, fieldUiId: string, mutate: (options: FormFieldOptionData[]) => FormFieldOptionData[]) => {
      updateSectionFields(sectionUiId, (fields) =>
        fields.map((f) => {
          if (f.uiId !== fieldUiId) return f;
          return { ...f, options: mutate(f.options ?? []) };
        }),
      );
    },
    [updateSectionFields],
  );

  const addOption = useCallback((sectionUiId: string, fieldUiId: string) => {
    updateFieldOptions(sectionUiId, fieldUiId, (opts) => [...opts, { value: '', label: '' }]);
  }, [updateFieldOptions]);

  const removeOption = useCallback((sectionUiId: string, fieldUiId: string, idx: number) => {
    updateFieldOptions(sectionUiId, fieldUiId, (opts) => opts.filter((_, i) => i !== idx));
  }, [updateFieldOptions]);

  const moveOption = useCallback((sectionUiId: string, fieldUiId: string, idx: number, delta: 1 | -1) => {
    updateFieldOptions(sectionUiId, fieldUiId, (opts) => move(opts, idx, idx + delta));
  }, [updateFieldOptions]);

  const updateOption = useCallback(
    (sectionUiId: string, fieldUiId: string, idx: number, patch: Partial<FormFieldOptionData>) => {
      updateFieldOptions(sectionUiId, fieldUiId, (opts) =>
        opts.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
      );
    },
    [updateFieldOptions],
  );

  // ── ShowIf / Formula ───────────────────────────────────────────
  const setShowIf = useCallback(
    (sectionUiId: string, fieldUiId: string, showIf: FormFieldShowIfData | undefined) => {
      updateSectionFields(sectionUiId, (fields) =>
        fields.map((f) => (f.uiId === fieldUiId ? { ...f, showIf } : f)),
      );
    },
    [updateSectionFields],
  );

  const setFormula = useCallback((sectionUiId: string, fieldUiId: string, formula: string) => {
    updateSectionFields(sectionUiId, (fields) =>
      fields.map((f) => (f.uiId === fieldUiId ? { ...f, formula } : f)),
    );
  }, [updateSectionFields]);

  // ── Columns (table type) ───────────────────────────────────────
  const updateColumnsOf = useCallback(
    (sectionUiId: string, fieldUiId: string, mutate: (columns: FieldDraft[]) => FieldDraft[]) => {
      updateSectionFields(sectionUiId, (fields) =>
        fields.map((f) => {
          if (f.uiId !== fieldUiId || f.type !== 'table') return f;
          return { ...f, columns: mutate(f.columns ?? []) };
        }),
      );
    },
    [updateSectionFields],
  );

  const addColumn = useCallback((sectionUiId: string, fieldUiId: string) => {
    updateColumnsOf(sectionUiId, fieldUiId, (cols) => [...cols, newFieldDraft('text')]);
  }, [updateColumnsOf]);

  const removeColumn = useCallback((sectionUiId: string, fieldUiId: string, columnUiId: string) => {
    updateColumnsOf(sectionUiId, fieldUiId, (cols) => cols.filter((c) => c.uiId !== columnUiId));
  }, [updateColumnsOf]);

  const moveColumn = useCallback(
    (sectionUiId: string, fieldUiId: string, columnUiId: string, delta: 1 | -1) => {
      updateColumnsOf(sectionUiId, fieldUiId, (cols) => {
        const idx = cols.findIndex((c) => c.uiId === columnUiId);
        if (idx < 0) return cols;
        return move(cols, idx, idx + delta);
      });
    },
    [updateColumnsOf],
  );

  const updateColumn = useCallback(
    (sectionUiId: string, fieldUiId: string, columnUiId: string, patch: Partial<FieldDraft>) => {
      updateColumnsOf(sectionUiId, fieldUiId, (cols) =>
        cols.map((c) => {
          if (c.uiId !== columnUiId) return c;
          let merged: FieldDraft = { ...c, ...patch };
          if (Object.prototype.hasOwnProperty.call(patch, 'label') && !merged.keyLocked) {
            merged.key = slugLabel(merged.label);
          }
          return merged;
        }),
      );
    },
    [updateColumnsOf],
  );

  const changeColumnType = useCallback(
    (sectionUiId: string, fieldUiId: string, columnUiId: string, type: FormFieldType) => {
      updateColumnsOf(sectionUiId, fieldUiId, (cols) =>
        cols.map((c) => (c.uiId === columnUiId ? applyTypeDefaults({ ...c, type }) : c)),
      );
    },
    [updateColumnsOf],
  );

  return {
    draft,
    setDraft,
    reset,
    setName,
    setAttachTo,
    addSection,
    removeSection,
    moveSection,
    updateSection,
    addField,
    removeField,
    moveField,
    updateField,
    changeFieldType,
    addOption,
    removeOption,
    moveOption,
    updateOption,
    setShowIf,
    setFormula,
    addColumn,
    removeColumn,
    moveColumn,
    updateColumn,
    changeColumnType,
  };
}
